/**
 * Ordering for PDF description pages (chunked comps text) so it matches:
 * - Excel valuation export (`ValuationReport` public comps: sort key `(order is None, order)`;
 *   transaction comps: `GPTReportSerializer` partition + sort).
 * - The GPC spreadsheet’s logical column order (`getRowConfig` sorts by `order`).
 *
 * PDF table screenshots still come from the DOM; these helpers apply only to description lists.
 */

const isNullish = (value: number | null | undefined): value is null | undefined =>
  [null, undefined].includes(value as null | undefined);

const compareNumericId = (a: number | null | undefined, b: number | null | undefined): number => {
  const aNum = isNullish(a) ? 0 : Number(a);
  const bNum = isNullish(b) ? 0 : Number(b);
  return aNum - bNum;
};

/**
 * Mirrors `apps/valuations/reports/reports.py` public comps:
 * `sorted(gpc_comparison, key=lambda x: (x.get('order') is None, x.get('order')))`.
 */
export function sortGpcComparisonForPdfDescriptions<T extends { order?: number | null; id?: number | null }>(
  comparisons: readonly T[]
): T[] {
  return [...comparisons].sort((a, b) => {
    const aNone = isNullish(a.order);
    const bNone = isNullish(b.order);
    if (aNone !== bNone) {
      return (aNone ? 1 : 0) - (bNone ? 1 : 0);
    }
    if (!aNone && !bNone && a.order !== b.order) {
      return (a.order as number) - (b.order as number);
    }
    return compareNumericId(a.id, b.id);
  });
}

/**
 * Mirrors `apps/valuations/serializers/gpt.py` `GPTReportSerializer.to_representation`:
 * rows with `order` first (sorted by `order`), then rows without `order` (sorted by `id`).
 */
export function sortGptTransactionsForPdfDescriptions<T extends { order?: number | null; id?: number | null }>(
  transactions: readonly T[]
): T[] {
  const withOrder = transactions.filter(t => !isNullish(t.order));
  const withoutOrder = transactions.filter(t => isNullish(t.order));
  withOrder.sort((a, b) => (a.order as number) - (b.order as number));
  withoutOrder.sort((a, b) => compareNumericId(a.id, b.id));
  return [...withOrder, ...withoutOrder];
}
